import { useMemo, type ReactNode } from 'react';
import { Stack, Typography, useMediaQuery } from '@mui/material';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import { useAdminPageChrome, type AdminPageChromeState } from '../../context/AdminPageChromeContext';

export function AdminListPageHeader({
  title,
  subtitle,
  endAdornment,
}: {
  title: string;
  subtitle?: string;
  endAdornment?: ReactNode;
}) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const chrome = useMemo<AdminPageChromeState>(
    () => ({
      title,
      subtitle,
      endAdornment,
    }),
    // endAdornment should be memoized by callers when used
    [title, subtitle, endAdornment],
  );

  useAdminPageChrome(chrome);

  // Desktop: title lives in the sticky Layout top bar.
  if (isMdUp) return null;

  return (
    <Stack
      direction="column"
      alignItems="flex-start"
      gap={1}
      sx={{ mb: 2 }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        sx={{ width: 1 }}
      >
        <BoxTitle title={title} />
        {endAdornment}
      </Stack>
      {subtitle ? (
        <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 560, lineHeight: 1.55 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Stack>
  );
}

function BoxTitle({ title }: { title: string }) {
  const theme = useTheme();
  return (
    <Typography
      variant="h5"
      sx={{
        fontWeight: 800,
        letterSpacing: -0.02,
        background: `linear-gradient(120deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
      }}
    >
      {title}
    </Typography>
  );
}

/** Matches Layout side nav — solid deep blue-purple (indigo-900). */
export const ADMIN_NAV_SURFACE = '#312E81';

/** Applications filter panel — grey chrome (header + near-white body). */
export const ADMIN_FILTER_SURFACE = '#F1F5F9'; // slate-100
export const ADMIN_FILTER_HEADER = '#475569'; // slate-600 — white title

/** Floating filter popover paper (does not push list content down). */
export function filtersPopoverPaperSx(theme: Theme) {
  const isLight = theme.palette.mode === 'light';
  const header = isLight ? ADMIN_FILTER_HEADER : '#94A3B8';
  const body = isLight ? ADMIN_FILTER_SURFACE : alpha('#94A3B8', 0.12);
  const fieldBorder = alpha(theme.palette.text.primary, isLight ? 0.18 : 0.35);

  return {
    width: { xs: 'min(100vw - 24px, 420px)', sm: 420 },
    maxHeight: 'min(78vh, 640px)',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 2.5,
    border: `1.5px solid ${alpha(header, isLight ? 0.35 : 0.5)}`,
    overflow: 'hidden',
    bgcolor: 'background.paper',
    boxShadow: isLight
      ? `0 18px 48px ${alpha('#101828', 0.16)}, 0 2px 6px ${alpha('#101828', 0.06)}`
      : `0 18px 48px ${alpha('#000', 0.55)}`,
    '& > .MuiBox-root:first-of-type': {
      bgcolor: header,
      borderBottom: `1px solid ${alpha('#000', 0.16)}`,
      color: '#fff',
      flexShrink: 0,
      '& .MuiTypography-root': { color: '#fff', fontWeight: 800 },
      '& .MuiIconButton-root, & .MuiButton-root': {
        color: 'rgba(255,255,255,0.92)',
        '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
      },
      '& .MuiSvgIcon-root': { color: 'inherit' },
    },
    '& > .MuiBox-root:nth-of-type(2)': {
      bgcolor: body,
      overflow: 'auto',
      flex: '1 1 auto',
    },
    '& > .MuiBox-root:last-of-type': {
      bgcolor: body,
      borderTop: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
      flexShrink: 0,
    },
    '& .MuiOutlinedInput-root': {
      bgcolor: theme.palette.background.paper,
      transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
      '& fieldset': {
        borderColor: fieldBorder,
        borderWidth: '1.5px',
      },
      '&:hover fieldset': {
        borderColor: alpha(header, isLight ? 0.55 : 0.7),
      },
      '&.Mui-focused': {
        boxShadow: `0 0 0 3px ${alpha(header, isLight ? 0.2 : 0.35)}`,
      },
      '&.Mui-focused fieldset': {
        borderColor: header,
        borderWidth: '1.5px',
      },
    },
    '& .MuiInputLabel-root': {
      fontWeight: 600,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: header,
    },
  };
}

/** @deprecated Prefer filtersPopoverPaperSx — kept for any leftover card layouts. */
export function filtersPanelCardSx(theme: Theme) {
  return { ...filtersPopoverPaperSx(theme), mb: 2, width: 'auto', maxHeight: 'none' };
}

/** @deprecated Prefer ADMIN_FILTER_SURFACE / ADMIN_FILTER_HEADER */
export const ADMIN_FILTER_SKY = ADMIN_FILTER_SURFACE;
/** @deprecated Prefer ADMIN_FILTER_HEADER */
export const ADMIN_FILTER_SKY_HEADER = ADMIN_FILTER_HEADER;

export function dataTableCardSx(theme: Theme) {
  return {
    borderRadius: 3,
    overflow: 'hidden',
    border: '1px solid',
    borderColor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.22 : 0.35),
    boxShadow:
      theme.palette.mode === 'light'
        ? `0 12px 40px ${alpha(theme.palette.primary.main, 0.06)}`
        : `0 12px 48px ${alpha('#000', 0.35)}`,
  };
}

export function adminDataTableSx(theme: Theme) {
  const stripe = alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.035 : 0.08);
  const headerBg = alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.09 : 0.18);
  return {
    '& .MuiTableCell-root': {
      fontSize: '0.78rem',
      fontWeight: 600,
      py: 1.1,
      verticalAlign: 'middle',
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      fontWeight: 800,
      fontSize: '0.68rem',
      letterSpacing: '0.08em',
      textTransform: 'uppercase' as const,
      color: theme.palette.text.primary,
      bgcolor: headerBg,
      borderBottom: `2px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.22 : 0.4)}`,
      whiteSpace: 'nowrap' as const,
      py: 1.35,
    },
    '& .MuiTableSortLabel-root': {
      color: 'inherit',
      fontWeight: 800,
      fontSize: 'inherit',
      letterSpacing: 'inherit',
    },
    '& .MuiTableSortLabel-icon': {
      opacity: 0.55,
    },
    '& .MuiTableSortLabel-root.Mui-active': {
      color: theme.palette.primary.main,
    },
    '& .MuiTableSortLabel-root.Mui-active .MuiTableSortLabel-icon': {
      opacity: 1,
      color: theme.palette.primary.main,
    },
    '& .MuiTableSortLabel-root:hover': {
      color: theme.palette.primary.main,
    },
    '& .MuiTableSortLabel-root:hover .MuiTableSortLabel-icon': {
      opacity: 1,
    },
    '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)': {
      bgcolor: stripe,
    },
    '& .MuiTableBody-root .MuiTableRow-root:hover': {
      bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.07 : 0.14),
    },
    '& .MuiTableBody-root .MuiTableRow-root.Mui-selected': {
      bgcolor: alpha(theme.palette.action.selected, 0.5),
    },
  };
}

export function monoLinkSx(theme: Theme) {
  return {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
    textDecoration: 'none',
    wordBreak: 'break-all' as const,
    '&:hover': { textDecoration: 'underline' },
  };
}

export const listPageFadeIn = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] as const },
};

export const listSectionFadeIn = (delay = 0) => ({
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.28, delay, ease: [0.22, 1, 0.36, 1] as const },
});
